import { useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { PlainTextDocIcon } from '../../svgs/PlainTextDocIcon';
import { RichTextDocIcon } from '../../svgs/RichTextDocIcon';
import ShareIcon from '../../svgs/ShareIcon';
import { useOutsideAlerter } from '../transcript/useClickOutside';

type Props = {
  type?: 'Blog' | 'Social Post' | 'Email';
  padding?: string;
  color?: string;
  bgColor?: string;
  onCopy?: () => void;
  onCopyText?: () => void;
};

const ContentShare = (props: Props) => {
  const {
    // Horizontal padding follows the Arbor primary-button standard:
    // var(--btn-px-md) ≈ 14px — tight enough that the button sizes to content.
    padding = '0 var(--btn-px-md, 14px)',
    // Default primary look — filled green, matching Produce Clips.
    // Callers can still override by passing explicit color / bgColor.
    color = 'var(--background)',
    bgColor = 'var(--primary)',
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    type = 'Blog',
    onCopy = () => {},
    onCopyText = () => {},
  } = props;

  const [openDropdown, toggleDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter(dropdownRef, () => {
    toggleDropdown(false);
  });

  return (
    <Main ref={dropdownRef}>
      <Button
        padding={padding}
        color={color}
        bgColor={bgColor}
        onClick={() => {
          toggleDropdown(!openDropdown);
        }}
      >
        <ShareIcon strokeColor={color} width="16" height="16" />{' '}
        <span>Share</span>
        <Chevron isOpen={openDropdown}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path
              d="M2.5 3.75L5 6.25L7.5 3.75"
              stroke={color}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Chevron>
      </Button>

      {openDropdown && (
        <Dropdown role="menu" aria-label="Share options">
          <DropdownHeader>Share</DropdownHeader>
          <Row
            role="menuitem"
            onClick={() => {
              onCopyText();
              toggleDropdown(false);
            }}
          >
            <RowIcon>
              <PlainTextDocIcon />
            </RowIcon>
            <RowContent>
              <RowLabel>Copy plain text</RowLabel>
              <RowSubtext>Strips formatting</RowSubtext>
            </RowContent>
          </Row>
          <Row
            role="menuitem"
            onClick={() => {
              onCopy();
              toggleDropdown(false);
            }}
          >
            <RowIcon>
              <RichTextDocIcon />
            </RowIcon>
            <RowContent>
              <RowLabel>Copy with formatting</RowLabel>
              <RowSubtext>Keeps styles and layout</RowSubtext>
            </RowContent>
          </Row>
        </Dropdown>
      )}
    </Main>
  );
};

export default ContentShare;

const Chevron = styled.span<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: ${props => (props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const Main = styled.div`
  position: relative;
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 240px;
  padding: 6px;
  background: var(--popover);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 2px;
  animation: ${slideIn} 0.15s ease-out;
`;

const DropdownHeader = styled.div`
  padding: 6px 10px 4px;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  outline: 0;
  cursor: pointer;
  text-align: left;
  color: var(--foreground);
  transition: background-color 0.12s ease;

  &:hover {
    background: var(--sidebar-hover);
  }

  &:focus-visible {
    background: var(--sidebar-hover);
    box-shadow: 0 0 0 1.5px var(--primary);
  }

  &:active {
    background: var(--surface-elevated);
  }
`;

const RowIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  color: var(--text-muted);
  background: var(--surface);
  border-radius: 6px;

  ${Row}:hover & {
    color: var(--foreground);
  }
`;

const RowContent = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RowLabel = styled.span`
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: 1.2;
`;

const RowSubtext = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  line-height: 1.2;
`;

/**
 * Share-button trigger styled to match the Produce Clips primary action:
 *   - filled green (--primary) background
 *   - 1px matching border
 *   - 14px bold label + chevron
 *   - 36px tall (var(--btn-h-md)) so it aligns with "Generate New" next to it
 * Hover / active / focus / disabled states layered per the Arbor primary tier.
 */
export const Button = styled.button<{
  padding: string;
  color: string;
  bgColor?: string;
}>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: ${props => props.padding};
  /* Width sizes to content — no forced min-width. Mirrors the Arbor primary
     button standard so it never looks over-padded next to compact peers. */
  height: var(--btn-h-md, 36px);
  border-radius: 8px;
  outline: 0;
  font-size: var(--text-sm);
  font-weight: 700;
  background: ${props => props.bgColor};
  color: ${props => props.color};
  border: 1px solid
    ${props => (props.bgColor === 'transparent' ? props.color : props.bgColor)};
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.05s ease;

  span {
    color: ${props => props.color};
  }

  &:hover:not(:disabled) {
    background: var(--primary-hover, var(--primary));
    border-color: var(--primary-hover, var(--primary));
  }

  &:active:not(:disabled) {
    background: var(--primary-pressed, var(--primary-hover, var(--primary)));
    border-color: var(--primary-pressed, var(--primary-hover, var(--primary)));
    transform: translateY(0.5px);
  }

  &:focus-visible {
    box-shadow:
      0 0 0 2px var(--background),
      0 0 0 4px var(--primary-hover, var(--primary));
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
